import axios from 'axios';
import { RolePermission, User } from '../types';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
export const PUBLIC_API_ROOT = API_URL.replace(/\/api\/?$/, '');

const api = axios.create({
  baseURL: API_URL,
});

function getViewId() {
  const existing = sessionStorage.getItem('glsn-view-id');
  if (existing) return existing;
  const generated = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  sessionStorage.setItem('glsn-view-id', generated);
  return generated;
}

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.dispatchEvent(new Event('auth:logout'));
    }
    return Promise.reject(error);
  },
);

export const authService = {
  login: (username: string, password: string) =>
    api.post('/auth/login', { username, password }),
  register: (username: string, password: string, full_name: string) =>
    api.post('/auth/register', { username, password, full_name }),
  createInvite: (role: 'admin' | 'staff' | 'member' | 'robot') => api.post('/auth/invites', { role }),
  listInvites: () => api.get('/auth/invites'),
  getInvite: (token: string) => api.get(`/auth/invites/${token}`),
  acceptInvite: (token: string, username: string, password: string, full_name: string) =>
    api.post('/auth/accept-invite', { token, username, password, full_name }),
  profile: () => api.get('/auth/profile'),
  getSettings: () => api.get('/auth/settings'),
  updateSettings: (settings: {
    theme?: 'light' | 'dark' | 'storm' | 'midnight' | 'emerald' | 'contrast';
    time_format?: '12h' | '24h';
    timezone?: string;
    alert_sound_enabled?: boolean;
    desktop_notifications_enabled?: boolean;
    active_alerts_compact?: boolean;
    show_polygon_data_by_default?: boolean;
  }) => api.put('/auth/settings', { settings }),
  updateAccount: (payload: {
    username?: string;
    full_name?: string;
    current_password: string;
    new_password?: string;
  }) => api.put('/auth/account', payload),
};

export const discussionService = {
  getAll: (status?: string, limit?: number, offset?: number) =>
    api.get('/discussions', { params: { status, limit, offset } }),
  getByPermalink: (permalink: string) =>
    api.get(`/discussions/${permalink}`, { headers: { 'X-View-Id': getViewId() } }),
  create: (payload: { title: string; concerning_text?: string | null; summary: string | null; discussion_body: string | null; status?: string; tags?: string[]; hazard_tags?: string[]; tornado_probability?: string | null; tornado_peak_intensity?: string | null; wind_probability?: string | null; hail_probability?: string | null; area_geojson?: string | null; highlight_color?: string | null; valid_from_utc?: string | null; valid_to_utc?: string | null }) =>
    api.post('/discussions', payload),
  update: (id: string, payload: { title?: string; concerning_text?: string | null; summary?: string | null; discussion_body?: string | null; status?: string; tags?: string[]; hazard_tags?: string[]; tornado_probability?: string | null; tornado_peak_intensity?: string | null; wind_probability?: string | null; hail_probability?: string | null; area_geojson?: string | null; highlight_color?: string | null; valid_from_utc?: string | null; valid_to_utc?: string | null }) =>
    api.put(`/discussions/${id}`, payload),
  delete: (id: string) =>
    api.delete(`/discussions/${id}`),
  search: (q: string, limit?: number, offset?: number) =>
    api.get('/discussions/search', { params: { q, limit, offset } }),
  transition: (id: string, status: string, reviewNote?: string) =>
    api.put(`/discussions/${id}`, { status, ...(reviewNote ? { review_note: reviewNote } : {}) }),
  history: (id: string) => api.get<DiscussionHistoryEntry[]>(`/discussions/${id}/history`),
  addNote: (id: string, note: string) => api.post<DiscussionHistoryEntry>(`/discussions/${id}/notes`, { note }),
  duplicate: (id: string) => api.post(`/discussions/${id}/duplicate`),
  exportText: (id: string, format: 'text' | 'x') =>
    api.get<{ format: string; text: string; characters: number }>(`/discussions/${id}/export`, { params: { format } }),
  areaCheck: (areaGeoJson: string) =>
    api.post<AreaCheck>('/discussions/area-check', { area_geojson: areaGeoJson }),
};

export interface DiscussionHistoryEntry {
  id: string;
  actor_name: string | null;
  action: string;
  from_status: string | null;
  to_status: string | null;
  note: string | null;
  created_at: string;
}

export interface AreaCheck {
  states: string[];
  points: number;
  outside_points: number;
  warnings: string[];
}

export interface WebhookEventConfig {
  key: string;
  label: string;
  description: string;
  enabled: boolean;
  channel: 'staff' | 'public' | 'custom';
  custom_url_masked: string | null;
  mention_users: string[];
  mention_roles: string[];
  has_destination: boolean;
}

export interface WebhookConfig {
  enabled: boolean;
  staff_url_masked: string | null;
  public_url_masked: string | null;
  default_mention_user_id: string | null;
  public_role_id: string | null;
  public_base_url: string | null;
  sources: Record<'staff_url' | 'public_url' | 'mention' | 'public_role', 'env' | 'file' | null>;
  events: WebhookEventConfig[];
}

export interface WebhookDelivery {
  at: string;
  event: string;
  test_event?: string;
  discussion_title: string | null;
  channel: string;
  destination: string | null;
  ok: boolean;
  status: number | null;
  attempts: number;
  error?: string;
}

export const webhookService = {
  get: () => api.get<WebhookConfig>('/webhooks'),
  update: (payload: Record<string, unknown>) => api.put<WebhookConfig>('/webhooks', payload),
  test: (event: string) => api.post<{ ok: boolean; error?: string }>('/webhooks/test', { event }),
  deliveries: (limit = 50) => api.get<WebhookDelivery[]>('/webhooks/deliveries', { params: { limit } }),
};

// templates are deprecated in this deployment — templateService removed

export const userService = {
  list: () => api.get('/users'),
  updateRole: (id: string, role: 'owner' | 'admin' | 'staff' | 'member' | 'robot') => api.put(`/users/${id}/role`, { role }),
  delete: (id: string, reason: string) => api.delete(`/users/${id}`, { data: { reason } }),
};

export const rolePermissionService = {
  list: () => api.get<{ roles: Array<{ role: User['role']; permissions: RolePermission[] }>; definitions: Array<{ key: RolePermission; label: string; group: string }> }>('/users/role-permissions'),
  update: (role: Exclude<User['role'], 'owner'>, permissions: RolePermission[]) =>
    api.put<{ role: string; permissions: RolePermission[] }>(`/users/role-permissions/${role}`, { permissions }),
};

export const severeWeatherReportService = {
  create: (payload: {
    report_type: string;
    damage_type: string;
    location_description: string;
    occurred_at: string;
    report_details: string;
    reporter_name?: string;
    attachments: File[];
  }) => {
    const formData = new FormData();
    formData.append('report_type', payload.report_type);
    formData.append('damage_type', payload.damage_type);
    formData.append('location_description', payload.location_description);
    formData.append('occurred_at', payload.occurred_at);
    formData.append('report_details', payload.report_details);

    if (payload.reporter_name) {
      formData.append('reporter_name', payload.reporter_name);
    }

    payload.attachments.forEach((file) => formData.append('attachments', file));

    return api.post('/reports', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  list: () => api.get('/reports'),
  updateStatus: (id: string, status: 'submitted' | 'reviewing' | 'verified' | 'dismissed') => api.put(`/reports/${id}/status`, { status }),
  delete: (id: string, reason: string) => api.delete(`/reports/${id}`, { data: { reason } }),
};

export const nwsAlertService = {
  listActive: () => api.get<{ updated_at: string; alerts: import('../types').NwsAlert[] }>('/alerts'),
};

export default api;
